import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  BehaviorSubject,
  Subject,
  filter,
  map,
  take,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { GlobalContextState, BaseEvent, EventName } from '../types';
import { distinctUntilChanged } from 'rxjs/operators';

const EVENT_ENDPOINT = '/edge/events/';

interface CompletedEvents {
  name: EventName;
  id: string;
  data?: any;
}

@Injectable({
  providedIn: 'root',
})
export class EventService {
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // In memory storage for context
  public context$: BehaviorSubject<GlobalContextState> =
    new BehaviorSubject<GlobalContextState>({
      SessionStartedId: null,
    });

  // stream of events as they fire
  public completed$: Subject<CompletedEvents> = new Subject<CompletedEvents>();

  constructor(private http: HttpClient) {
    // this is a client side feature at the moment, maybe once
    // we completely move auth to server side we could support server side
    // eventing.  it would be nice as it removes load from the user's device
    // if we want to fire some eventing about the request itself
    if (!this.isBrowser) {
      return;
    }
  }

  public send(e: BaseEvent): void {
    this.context$
      .pipe(
        // some events need to wait for other events to provide their context before they fire and as different async operations
        // with the app happens and add to this global context, the event will continue to listen to these updates until
        // it reaches a point where it's required context like session id, app instance, etc are all satisfied then proceed
        filter((context: any) => {
          const requiredContext = e.getRequiredContext();
          let isMissingContext = false;

          requiredContext.forEach((key: string) => {
            // validate that this event can fill in all it's required context
            if (
              !(
                key in context &&
                context[key] !== undefined &&
                context[key] !== null &&
                context[key].length > 0
              )
            ) {
              isMissingContext = true;
            }
          });

          return !isMissingContext;
        }),
        // once we satisfy all required context, just take the first one and fire the event
        take(1),
        // once this event has all the joining ids to faciliate it, update global context with this new id
        // so events after this can map to this new id as the latest "thing" that happened for a specific context
        tap(() => {
          if (e.getContext()) {
            this.updateContext(e.getContext());
          }
        }),
        timeout({
          each: 15000,
          with: () =>
            throwError(
              () =>
                `Eventing: ${e.Name} failed to send while waiting for required context.`,
            ),
        }),
        map((context: GlobalContextState): { beacon: any; original: any } => {
          const payload = e.toPayload(context);
          const beaconPayload = {
            eventContent: payload,
            eventName: payload.Name,
          };
          delete beaconPayload.eventContent.Name;
          return { beacon: [beaconPayload], original: payload };
        }),
      )
      .subscribe((payload: { beacon: any; original: any }) => {
        if (payload.original.Debug) {
          console.log(payload.beacon);
        } else {
          this.http
            .post(EVENT_ENDPOINT, payload.beacon)
            .pipe(
              // on success notify the completed stream this event is confirmed sent
              tap(() =>
                this.completed$.next({
                  name: payload.beacon[0]?.eventName,
                  id: payload.beacon[0]?.eventContent?.EventId,
                  data: payload.original,
                }),
              ),
            )
            .subscribe();
        }
      });
  }

  public updateContext(context: object): void {
    this.context$.next(Object.assign(this.context$.getValue(), context));
  }
}
